<template>
    <v-container class="form-container">
        <page-header :title="pageTitle" :subtitle="isCreate ? null : user.email" :back="listPath">
            <v-btn v-if="isShow" color="primary" depressed @click="goEdit">
                <v-icon left>mdi-pencil-outline</v-icon>Edit
            </v-btn>
        </page-header>

        <v-form ref="form" v-model="valid" :readonly="readOnly" @submit.prevent="onSubmit">
            <v-card outlined class="mb-4" :loading="loadingEntity">
                <v-card-text>
                    <v-row dense>
                        <v-col cols="12" md="6">
                            <v-text-field
                                v-model.trim="user.email"
                                :rules="[...required, ...emailRules]"
                                label="Email *"
                                autocomplete="off"
                                prepend-inner-icon="mdi-email-outline"
                                outlined
                            />
                        </v-col>
                        <v-col v-if="!readOnly" cols="12" md="6">
                            <v-text-field
                                v-model="password"
                                :rules="isCreate ? required : []"
                                :append-icon="showPassword ? 'mdi-eye-off' : 'mdi-eye'"
                                :type="showPassword ? 'text' : 'password'"
                                :label="isCreate ? 'Password *' : 'New password'"
                                :hint="isEdit ? 'Leave empty to keep the current password' : ''"
                                persistent-hint
                                autocomplete="new-password"
                                prepend-inner-icon="mdi-lock-outline"
                                outlined
                                @click:append="showPassword = !showPassword"
                            />
                        </v-col>
                        <v-col cols="12" md="6">
                            <v-autocomplete
                                v-model="user.dealer"
                                :items="dealers"
                                :item-text="fullName"
                                item-value="id"
                                label="Dealer"
                                hint="Leave empty for an administrator account"
                                persistent-hint
                                prepend-inner-icon="mdi-handshake-outline"
                                :clearable="!readOnly"
                                return-object
                                outlined
                            />
                        </v-col>
                    </v-row>
                </v-card-text>
            </v-card>

            <div v-if="!readOnly" class="form-actions">
                <v-btn text @click="goBack">Cancel</v-btn>
                <v-btn color="primary" depressed large type="submit" :loading="saving" :disabled="!valid || loadingEntity">
                    <v-icon left>mdi-content-save-outline</v-icon>{{ isCreate ? 'Create user' : 'Save changes' }}
                </v-btn>
            </div>
        </v-form>
    </v-container>
</template>

<script>
    import userService from '@/services/user-service'
    import dealerService from '@/services/dealer-service'
    import formPage from '@/mixins/form-page'
    import PageHeader from '@/components/PageHeader'
    import { entityId, fullName } from '@/utils/format'

    export default {
        name: 'UserForm',
        components: { PageHeader },
        mixins: [formPage],
        data: () => {
            return {
                entityLabel: 'User',
                userId: null,
                showPassword: false,
                password: '',
                dealers: [],
                user: {
                    email: '',
                    dealer: null
                },
                emailRules: [
                    v => !v || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v) || 'Enter a valid email address'
                ]
            }
        },
        created() {
            dealerService.getAllDealers().then(resp => {
                this.dealers = resp.data.data
            })
            if (this.isCreate) {
                return
            }
            this.userId = parseInt(this.$route.params.userId)
            this.loadingEntity = true
            userService.getUserById(this.userId).then(resp => {
                const user = resp.data.user
                // the dealer has to be loaded too, otherwise saving turned dealer accounts into administrators
                this.user = { email: user.email, dealer: user.dealer && typeof user.dealer === 'object' ? user.dealer : null }
                if (user.dealer && typeof user.dealer !== 'object') {
                    this.user.dealer = { id: user.dealer }
                }
            }).catch(error => {
                this.$notify.error(error, 'Could not load the user')
            }).finally(() => {
                this.loadingEntity = false
            })
        },
        methods: {
            fullName,
            onSubmit() {
                if (!this.validate()) {
                    return
                }
                this.saving = true
                // UserType rejects unknown fields; an empty password keeps the current one
                const payload = {
                    email: this.user.email,
                    dealer: entityId(this.user.dealer)
                }
                if (this.password) {
                    payload.password = this.password
                }
                const request = this.isEdit
                    ? userService.editUserById(this.userId, payload)
                    : userService.createUser(payload)
                request.then(() => {
                    this.$notify.success(this.isCreate ? 'User created' : 'User saved')
                    this.goBack()
                }).catch(error => {
                    this.$notify.error(error, 'Could not save the user')
                }).finally(() => {
                    this.saving = false
                })
            }
        }
    }
</script>
